import { cloneElement, useMemo } from 'react';
import type React from 'react';

import { Alert } from '@change/ds-core-ui/Alert';
import { ErrorMessage } from '@change/ds-forms/FieldError';

import { useI18n } from '@change-corgi/core-react/i18n';

import { guardStripeFocusTracker } from '#src/app/shared/utils/payments';

import { SingleForm } from './components/SingleForm';
import { SplitForm } from './components/SplitForm';
import { useStripeForm } from './hooks/useStripeForm';
import type { StripeCreditCardLayoutProps } from './types';

export const StripeCreditCardLayout: React.FC<StripeCreditCardLayoutProps> = ({
	submitButton,
	children,
	labelWeight,
	displayErrorInAlert,
	phoneNumberInput,
	...rest
}) => {
	const {
		data: { errorMessage, validationErrors, errorType },
		actions: { handleSubmit, validateStripeElementOnChange, setValidationErrors },
	} = useStripeForm(rest);

	const { translate } = useI18n();

	const { errorTitle, errorDescription } = useMemo(() => {
		if (errorType === 'api_connection_error') {
			return {
				errorTitle: translate('corgi.payments.stripe_error.connection_error_heading'),
				errorDescription: errorMessage || translate('corgi.payments.stripe_error.generic_error_description'),
			};
		}
		return {
			errorTitle: translate('corgi.payments.stripe_error.generic_decline_error_heading'),
			errorDescription: translate('corgi.payments.stripe_error.generic_error_description'),
		};
	}, [errorType, errorMessage, translate]);

	return (
		<form onSubmit={handleSubmit}>
			<div className="flex flex-col gap-4">
				{children}
				{rest.layout === 'single' ? (
					<SingleForm
						{...rest}
						onCardDetailsFieldFocus={guardStripeFocusTracker(rest.onCardDetailsFieldFocus, 'card')}
						validationErrors={validationErrors}
						setValidationErrors={setValidationErrors}
					/>
				) : (
					<SplitForm
						{...rest}
						onCardNumberFieldFocus={guardStripeFocusTracker(rest.onCardNumberFieldFocus, 'cardNumber')}
						onCardExpiryFieldFocus={guardStripeFocusTracker(rest.onCardExpiryFieldFocus, 'cardExpiry')}
						onCardCVCFieldFocus={guardStripeFocusTracker(rest.onCardCVCFieldFocus, 'cardCvc')}
						validationErrors={validationErrors}
						validateStripeElementOnChange={validateStripeElementOnChange}
					/>
				)}
				{displayErrorInAlert && errorMessage && (
					<Alert variant="error" heading={errorTitle} data-qa="stripe-form-error-message">
						{errorDescription}
					</Alert>
				)}
				{phoneNumberInput}
				<div className="flex justify-center">
					{cloneElement(submitButton, {
						disabled: submitButton.props.disabled,
						loading: submitButton.props.loading,
					})}
				</div>
				{!displayErrorInAlert && errorMessage && (
					<div data-qa="stripe-form-error-message">
						<ErrorMessage size="large">{errorMessage}</ErrorMessage>
					</div>
				)}
			</div>
		</form>
	);
};
